import React, { useState, useEffect, useRef } from 'react';
import { Save, Eye, Send, ArrowLeft, Mail, Sparkles, Loader2, RotateCcw, Download, Upload, ScrollText, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Wand2, Layers, MailCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import EmailLayoutThumbnail from '../components/email/EmailLayoutThumbnail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailTemplatesPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [templateContent, setTemplateContent] = useState({
    subject: '',
    body: '',
    variables: []
  });

  const [reEngagementTemplates, setReEngagementTemplates] = useState(null);
  const [reEngagementLoading, setReEngagementLoading] = useState(false);
  const [reEngagementSaving, setReEngagementSaving] = useState(false);
  
  // Notification Logs state
  const [showLogs, setShowLogs] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateStatus, setTranslateStatus] = useState(null);

  const pollTranslateStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/templates/translate-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      const job = data.job;
      setTranslateStatus(job);
      if (job?.status === 'running') {
        setTranslating(true);
        setTimeout(pollTranslateStatus, 3000);
      } else {
        if (translating && job?.status === 'done') {
          alert(`✅ Übersetzung abgeschlossen!\n\n${job.translated} übersetzt, ${job.skipped} bereits aktuell, ${job.failed} Fehler`);
          fetchTemplates();
        }
        setTranslating(false);
      }
    } catch (e) { setTranslating(false); }
  };

  const [translatingConsent, setTranslatingConsent] = useState(false);

  const translateConsentForms = async () => {
    if (!window.confirm('Alle Einverständnis-Formulare (Kryo/EMS/Haftung) per KI in EN/FR/IT/ES/PT/SK/RU übersetzen?')) return;
    setTranslatingConsent(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/consent-forms/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) {
        alert('❌ ' + (data.detail || 'Fehler'));
      } else {
        const errs = (data.details || []).filter((d) => d.status === 'error');
        alert(`✅ Consent-Formulare übersetzt: ${data.translated}, Fehler: ${data.failed}` + (errs.length ? `\n\n${errs.map((e) => `${e.id}: ${e.error}`).join('\n')}` : ''));
      }
    } catch (e) {
      alert('❌ Fehler beim Übersetzen der Consent-Formulare');
    }
    setTranslatingConsent(false);
  };

  const startTranslation = async () => {
    if (!window.confirm('Alle E-Mail-Vorlagen per KI in EN/FR/IT/ES/PT/SK/RU übersetzen?\n\nBereits aktuelle Übersetzungen werden übersprungen.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/templates/translate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json();
        alert('❌ ' + (err.detail || 'Fehler'));
        return;
      }
      setTranslating(true);
      setTimeout(pollTranslateStatus, 2000);
    } catch (e) {
      alert('❌ Fehler beim Starten der Übersetzung');
    }
  };
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchNotificationTemplates();
    fetchReEngagementTemplates();
    fetchEmailLayouts();
  }, []);

  const [notificationTemplates, setNotificationTemplates] = useState([]);
  
  // AI Bulk Edit state
  const [showAIEditor, setShowAIEditor] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  
  // Backup state for rollback
  const [templateBackup, setTemplateBackup] = useState(null);

  // 07/2026: Vorlagen-Sicherheit, Live-Vorschau, KI-Variablen, Reset
  const [templateMeta, setTemplateMeta] = useState(null); // { required_variables, variable_meta, has_default, customized }
  const [renderedHtml, setRenderedHtml] = useState('');
  const [renderedSubject, setRenderedSubject] = useState('');
  const [rendering, setRendering] = useState(false);
  const [aiFilling, setAiFilling] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [emailLayouts, setEmailLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState('bold');

  // Ist die aktuelle Vorlage eine System-E-Mail-Vorlage (mit Pflicht-Variablen)?
  const isSystemEmailTemplate = (tpl) => tpl && !tpl._source;

  // Welche Pflicht-Variablen fehlen aktuell im Text? (live, wie Backend)
  const computeMissingRequired = () => {
    const required = templateMeta?.required_variables || [];
    const hay = `${templateContent.subject || ''} ${templateContent.body || ''}`;
    return required.filter((v) => !hay.includes(`{{${v}}}`) && !hay.includes(`{${v}}`));
  };

  const fetchTemplateMeta = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/email/templates/${templateId}/meta`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTemplateMeta(await res.json());
      else setTemplateMeta(null);
    } catch (e) {
      setTemplateMeta(null);
    }
  };

  const fetchEmailLayouts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/email/templates/layouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEmailLayouts(data.layouts || []);
        if (data.selected) setSelectedLayout(data.selected);
      }
    } catch (e) { /* ignore */ }
  };

  // Rendert die E-Mail mit Beispielwerten + Branding + Layout (reine Vorschau).
  const handleRenderPreview = async () => {
    setRendering(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/email/templates/render-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject: templateContent.subject,
          body: templateContent.body,
          variables: templateContent.variables,
          layout: selectedLayout,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setRenderedHtml(data.html || '');
        setRenderedSubject(data.subject || '');
        setPreviewMode(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`❌ Vorschau fehlgeschlagen: ${err.detail || res.statusText}`);
      }
    } catch (e) {
      alert(`❌ Vorschau fehlgeschlagen: ${e.message}`);
    } finally {
      setRendering(false);
    }
  };

  // KI setzt die passenden Variablen in den frei geschriebenen Text ein.
  const handleAiFill = async () => {
    setAiFilling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/email/templates/ai-fill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject: templateContent.subject,
          body: templateContent.body,
          variables: templateContent.variables,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setTemplateContent((prev) => ({ ...prev, subject: data.subject, body: data.body }));
        if ((data.missing_variables || []).length > 0) {
          alert(`⚠️ Die KI hat folgende Pflicht-Variablen noch nicht eingesetzt:\n${data.missing_variables.map((v) => `{{${v}}}`).join(', ')}\n\nBitte manuell ergänzen.`);
        } else {
          alert('✅ KI hat die Variablen eingesetzt. Prüfe den Text und speichere.');
        }
      } else {
        alert(`❌ KI-Fehler: ${data.detail || res.statusText}`);
      }
    } catch (e) {
      alert(`❌ KI-Fehler: ${e.message}`);
    } finally {
      setAiFilling(false);
    }
  };

  // Setzt die Vorlage auf den System-Standard zurück.
  const handleResetTemplate = async () => {
    if (!selectedTemplate || !window.confirm('Diese Vorlage auf den Standard-Text zurücksetzen? Deine Anpassungen gehen verloren.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/email/templates/${selectedTemplate.id}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setTemplateContent({ subject: data.subject, body: data.body, variables: data.variables || [] });
        setRenderedHtml('');
        fetchTemplateMeta(selectedTemplate.id);
        fetchTemplates();
        alert('✅ Auf Standard zurückgesetzt.');
      } else {
        alert(`❌ Zurücksetzen fehlgeschlagen: ${data.detail || res.statusText}`);
      }
    } catch (e) {
      alert(`❌ Zurücksetzen fehlgeschlagen: ${e.message}`);
    } finally {
      setResetting(false);
    }
  };


  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchNotificationTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/recurring-subscriptions/notification-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data is an array before filtering
        const templatesArray = Array.isArray(data) ? data : (data?.templates || data?.data || []);
        // Filter nur Email-Templates and mark source
        const hiddenTypes = ['low_points_warning_email', 'low_contingent_warning_email'];
        const emailTemplates = templatesArray
          .filter(t => t.template_type && t.template_type.includes('email') && !hiddenTypes.includes(t.template_type))
          .map(t => ({ ...t, _source: 'notification_templates' }));
        setNotificationTemplates(emailTemplates);
      }
    } catch (error) {
      console.error('Error fetching notification templates:', error);
    }
  };

  const fetchReEngagementTemplates = async () => {
    setReEngagementLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/settings/re-engagement/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReEngagementTemplates({
          email_template: data.email_template || { subject: '', body: '' },
          whatsapp_template_first: data.whatsapp_template_first || { template_name: '', body: '', language: 'de' },
          whatsapp_template_second: data.whatsapp_template_second || { template_name: '', body: '', language: 'de' }
        });
      }
    } catch (error) {
      console.error('Error fetching re-engagement templates:', error);
    } finally {
      setReEngagementLoading(false);
    }
  };

  const fetchNotificationLogs = async () => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/email/notification-logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setNotificationLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateContent({
      subject: template.subject,
      body: template.body,
      variables: template.variables || []
    });
    setEditMode(true);
    setPreviewMode(false);
    setRenderedHtml('');
    setTemplateMeta(null);
    if (isSystemEmailTemplate(template)) {
      fetchTemplateMeta(template.id);
    }
  };

  const saveTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (selectedTemplate._source === 're_engagement') {
        await saveReEngagementEmail({
          subject: templateContent.subject,
          body: templateContent.body
        });
        setEditMode(false);
        setSelectedTemplate(null);
        return;
      }
      
      // Check if it's from notification_templates collection
      // email_templates also have template_type, so we check for _source or use a different indicator
      const isNotificationTemplate = selectedTemplate._source === 'notification_templates';
      
      const url = isNotificationTemplate 
        ? `${BACKEND_URL}/api/recurring-subscriptions/notification-templates/${selectedTemplate.id}`
        : `${BACKEND_URL}/api/email/templates/${selectedTemplate.id}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateContent)
      });

      if (response.ok) {
        alert('✅ Template gespeichert!');
        fetchTemplates();
        if (isNotificationTemplate) {
          fetchNotificationTemplates();
        }
        setEditMode(false);
        setSelectedTemplate(null);
      } else if (response.status === 422) {
        // Pflicht-Variablen fehlen → Versand würde brechen. Speichern blockiert.
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail || {};
        const missing = detail.missing_variables || [];
        alert(
          `⚠️ Speichern nicht möglich – es fehlen Pflicht-Variablen:\n\n` +
          `${missing.map((m) => `{{${m}}}`).join(', ')}\n\n` +
          `Füge sie im Text ein oder nutze „KI-Variablen einsetzen".`
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`❌ Fehler beim Speichern: ${errorData.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Fehler beim Speichern');
    }
  };

  // Sendet eine Preview-Mail mit Dummy-Variablen an eine Adresse,
  // entweder mit dem gespeicherten DB-Template ODER mit den
  // ungespeicherten Editor-Werten (Live-Preview vor dem Speichern).
  const sendPreview = async (templateForPreview, useEditorContent = false) => {
    const defaultRecipient = localStorage.getItem('preview_email') || '';
    const recipient = window.prompt(
      'Empfänger für Preview-Mail:',
      defaultRecipient
    );
    if (!recipient) return;
    localStorage.setItem('preview_email', recipient.trim());

    try {
      const token = localStorage.getItem('token');
      const body = useEditorContent
        ? { recipient: recipient.trim(), subject: templateContent.subject, body: templateContent.body }
        : { recipient: recipient.trim() };

      const response = await fetch(
        `${BACKEND_URL}/api/email/templates/${templateForPreview.id}/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (response.ok) {
        alert(`📬 Preview an ${recipient.trim()} versendet!`);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`❌ Fehler: ${err.detail || response.statusText}`);
      }
    } catch (e) {
      alert(`❌ Fehler: ${e.message}`);
    }
  };

  // „Testmail an mich" — öffnet einen Dialog mit optionalen eigenen
  // Beispielwerten (Name, Link …) und sendet an eine Adresse (Standard:
  // der eingeloggte Admin). Backend mergt die Werte über die Dummies.
  const [sendingSelf, setSendingSelf] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testVars, setTestVars] = useState({});

  const openTestDialog = () => {
    const vars = templateMeta?.variables || templateContent.variables || [];
    const meta = templateMeta?.variable_meta || {};
    const init = {};
    vars.forEach((v) => { init[v] = meta[v]?.sample || ''; });
    setTestVars(init);
    setTestRecipient(user?.email || '');
    setShowTestDialog(true);
  };

  const sendTestEmail = async () => {
    if (!selectedTemplate) return;
    setSendingSelf(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        subject: templateContent.subject,
        body: templateContent.body,
        variables: testVars,
      };
      const rcpt = (testRecipient || '').trim();
      if (rcpt) body.recipient = rcpt;
      const response = await fetch(
        `${BACKEND_URL}/api/email/templates/${selectedTemplate.id}/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setShowTestDialog(false);
        alert(`📬 Testmail an ${data.recipient || rcpt || 'dich'} versendet! Prüfe deinen Posteingang (ggf. Spam).`);
      } else {
        alert(`❌ Fehler: ${data.detail || response.statusText}`);
      }
    } catch (e) {
      alert(`❌ Fehler: ${e.message}`);
    } finally {
      setSendingSelf(false);
    }
  };

  // Hover-Live-Vorschau der Layouts (echtes Logo + Akzentfarbe via Backend-Branding).
  const [hoverLayout, setHoverLayout] = useState(null);
  const [layoutPreviews, setLayoutPreviews] = useState({}); // { layoutId: html }
  const [layoutPreviewLoading, setLayoutPreviewLoading] = useState(null);

  const loadLayoutPreview = async (layoutId) => {
    if (layoutPreviews[layoutId] || layoutPreviewLoading === layoutId) return;
    setLayoutPreviewLoading(layoutId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/email/templates/render-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject: templateContent.subject,
          body: templateContent.body,
          variables: templateContent.variables,
          layout: layoutId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setLayoutPreviews((prev) => ({ ...prev, [layoutId]: data.html || '' }));
      }
    } catch (e) {
      // stillschweigend — Hover-Vorschau ist optional
    } finally {
      setLayoutPreviewLoading((cur) => (cur === layoutId ? null : cur));
    }
  };

  // Editor-Inhalt geändert → Hover-Vorschau-Cache verwerfen (bleibt aktuell).
  useEffect(() => {
    setLayoutPreviews({});
  }, [templateContent.subject, templateContent.body]);

  const saveReEngagementEmail = async (emailOverride) => {
    setReEngagementSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        email_template: emailOverride || reEngagementTemplates.email_template,
        whatsapp_template_first: reEngagementTemplates.whatsapp_template_first,
        whatsapp_template_second: reEngagementTemplates.whatsapp_template_second
      };
      const response = await fetch(`${BACKEND_URL}/api/settings/re-engagement/templates`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setReEngagementTemplates({
          email_template: data.email_template || payload.email_template,
          whatsapp_template: data.whatsapp_template || payload.whatsapp_template
        });
        alert('✅ Re-Engagement Email Vorlage gespeichert!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`❌ Fehler beim Speichern: ${errorData.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Error saving re-engagement template:', error);
      alert('Fehler beim Speichern der Re-Engagement Vorlage');
    } finally {
      setReEngagementSaving(false);
    }
  };

  const renderPreview = () => {
    let preview = templateContent.body;
    templateContent.variables.forEach((variable, index) => {
      const placeholder = `{{${variable}}}`;
      const exampleValue = `[Beispiel ${variable}]`;
      preview = preview.replace(new RegExp(placeholder, 'g'), exampleValue);
    });
    return preview;
  };

  // AI Bulk Edit Handler
  const handleAIBulkEdit = async () => {
    if (!aiPrompt.trim()) {
      alert('Bitte geben Sie einen Befehl ein.');
      return;
    }
    
    setAiProcessing(true);
    setAiPreview(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare templates for AI
      const allTemplates = [...templates, ...notificationTemplates];
      const templatesForAI = allTemplates.map(t => ({
        id: t.id,
        name: t.name,
        subject: t.subject,
        body: t.body
      }));
      
      // Call AI endpoint
      const response = await fetch(`${BACKEND_URL}/api/email/templates/ai-bulk-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          templates: templatesForAI
        })
      });
      
      if (!response.ok) {
        throw new Error('KI-Anfrage fehlgeschlagen');
      }
      
      const result = await response.json();
      
      if (result.success && result.modified_templates) {
        setAiPreview(result);
      } else {
        alert('Die KI konnte keine Änderungen vornehmen: ' + (result.message || 'Unbekannter Fehler'));
      }
    } catch (error) {
      console.error('AI bulk edit error:', error);
      alert('❌ Fehler bei der KI-Verarbeitung: ' + error.message);
    } finally {
      setAiProcessing(false);
    }
  };

  // Apply AI Changes
  const applyAIChanges = async () => {
    if (!aiPreview || !aiPreview.modified_templates) return;
    
    // Save backup before applying changes
    setTemplateBackup({
      templates: [...templates],
      notificationTemplates: [...notificationTemplates],
      timestamp: new Date().toISOString()
    });
    
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      
      for (const modified of aiPreview.modified_templates) {
        // Find if it's a notification template or regular template
        const isNotification = notificationTemplates.some(t => t.id === modified.id);
        
        const url = isNotification 
          ? `${BACKEND_URL}/api/recurring-subscriptions/notification-templates/${modified.id}`
          : `${BACKEND_URL}/api/email/templates/${modified.id}`;
        
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subject: modified.subject,
            body: modified.body
          })
        });
        
        if (response.ok) {
          successCount++;
        }
      }
      
      setAiPreview(null);
      setAiPrompt('');
      setShowAIEditor(false);
      
      // Refresh templates
      fetchTemplates();
      fetchNotificationTemplates();
      
      alert(`✅ ${successCount} von ${aiPreview.modified_templates.length} Templates wurden aktualisiert!`);
    } catch (error) {
      console.error('Error applying AI changes:', error);
      alert('❌ Fehler beim Anwenden der Änderungen: ' + error.message);
    }
  };

  // Rollback to previous version
  const handleRollback = async () => {
    if (!templateBackup) {
      alert('Keine Backup-Version verfügbar. Die Rollback-Funktion ist nur direkt nach einer KI-Änderung verfügbar.');
      return;
    }
    
    if (!window.confirm(`Möchten Sie zur vorherigen Version zurückkehren?\n\nBackup vom: ${new Date(templateBackup.timestamp).toLocaleString('de-CH')}`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      
      // Restore regular templates
      for (const template of templateBackup.templates) {
        const response = await fetch(`${BACKEND_URL}/api/email/templates/${template.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subject: template.subject,
            body: template.body
          })
        });
        
        if (response.ok) {
          successCount++;
        }
      }
      
      // Restore notification templates
      for (const template of templateBackup.notificationTemplates) {
        const response = await fetch(`${BACKEND_URL}/api/recurring-subscriptions/notification-templates/${template.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subject: template.subject,
            body: template.body
          })
        });
        
        if (response.ok) {
          successCount++;
        }
      }
      
      setTemplateBackup(null);
      fetchTemplates();
      fetchNotificationTemplates();
      
      alert(`✅ ${successCount} Templates wurden wiederhergestellt!`);
    } catch (error) {
      console.error('Error rolling back:', error);
      alert('❌ Fehler beim Wiederherstellen: ' + error.message);
    }
  };

  // ============== EXPORT / IMPORT ==============
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [automationRules, setAutomationRules] = useState([]);
  
  // Fetch automation rules for export
  const fetchAutomationRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/automation/rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAutomationRules(data);
      }
    } catch (error) {
      console.error('Error fetching automation rules:', error);
    }
  };

  // Export all templates AND automation rules as JSON
  const handleExportTemplates = async () => {
    // First fetch automation rules
    let rules = automationRules;
    if (rules.length === 0) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/automation/rules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Handle both {rules: [...]} and [...] responses
          rules = Array.isArray(data) ? data : (data.rules || []);
        }
      } catch (error) {
        console.error('Error fetching automation rules for export:', error);
      }
    }
    
    // Fetch WhatsApp local templates
    let whatsappTemplates = [];
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both {data: [...]} and direct array responses
        whatsappTemplates = data.data || data.local_templates || data.templates || [];
        if (!Array.isArray(whatsappTemplates)) {
          whatsappTemplates = [];
        }
      }
    } catch (error) {
      console.error('Error fetching WhatsApp templates for export:', error);
    }
    
    // Ensure rules is an array
    const rulesArray = Array.isArray(rules) ? rules : [];
    
    const exportData = {
      version: '3.0',
      exportedAt: new Date().toISOString(),
      exportedFrom: window.location.hostname,
      templates: templates.map(t => ({
        id: t.id,
        name: t.name,
        subject: t.subject,
        body: t.body,
        variables: t.variables || []
      })),
      notificationTemplates: notificationTemplates.map(t => ({
        id: t.id,
        name: t.name,
        subject: t.subject,
        body: t.body,
        template_type: t.template_type
      })),
      automationRules: rulesArray.map(r => ({
        id: r.id,
        name: r.name,
        trigger_event: r.trigger_event || r.trigger,
        email_enabled: r.email_enabled,
        email_template: r.email_template || r.email_template_id,
        whatsapp_enabled: r.whatsapp_enabled,
        whatsapp_template: r.whatsapp_template,
        whatsapp_template_name: r.whatsapp_template_name,
        whatsapp_language: r.whatsapp_language,
        enabled: r.enabled !== false
      })),
      whatsappTemplates: whatsappTemplates.map(t => ({
        id: t.id,
        name: t.name,
        template_id: t.template_id,
        language: t.language,
        category: t.category,
        body: t.body,
        header_text: t.header_text,
        footer_text: t.footer_text,
        buttons: t.buttons,
        variables: t.variables
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `strongfit-config-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert(`✅ Export abgeschlossen!\n\n📧 ${templates.length} E-Mail-Vorlagen\n⚡ ${rulesArray.length} Automation-Regeln\n💬 ${whatsappTemplates.length} WhatsApp-Vorlagen`);
  };

  // Import templates from JSON file
  const handleImportTemplates = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      // Validate import data
      if (!importData.templates || !Array.isArray(importData.templates)) {
        throw new Error('Ungültiges Dateiformat: templates fehlt');
      }

      const confirmMessage = `Import von ${importData.exportedFrom || 'unbekannt'}\n\n` +
        `📧 ${importData.templates?.length || 0} E-Mail-Vorlagen\n` +
        `⚡ ${importData.automationRules?.length || 0} Automation-Regeln\n` +
        `💬 ${importData.whatsappTemplates?.length || 0} WhatsApp-Vorlagen\n\n` +
        `⚠️ Bestehende Konfigurationen werden überschrieben!\n\n` +
        `Fortfahren?`;

      if (!window.confirm(confirmMessage)) {
        setImporting(false);
        return;
      }

      const token = localStorage.getItem('token');
      let successCount = 0;
      let createdCount = 0;
      let errorCount = 0;
      let duplicatesDeleted = 0;

      // Use batch import endpoint for regular templates
      try {
        const response = await fetch(`${BACKEND_URL}/api/email/templates/import`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            templates: importData.templates,
            notification_templates: importData.notificationTemplates || [],
            cleanup_duplicates: true  // Auto-cleanup duplicate notification_templates
          })
        });

        if (response.ok) {
          const result = await response.json();
          successCount += result.success_count || 0;
          createdCount += result.created_count || 0;
          duplicatesDeleted = result.duplicates_deleted || 0;
          
          if (result.errors && result.errors.length > 0) {
            console.warn('Import warnings:', result.errors);
          }
        } else {
          // Fallback to individual PUT requests if batch endpoint fails
          console.warn('Batch import failed, falling back to individual requests');
          for (const template of importData.templates) {
            try {
              const putResponse = await fetch(`${BACKEND_URL}/api/email/templates/${template.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  subject: template.subject,
                  body: template.body
                })
              });

              if (putResponse.ok) {
                successCount++;
              } else {
                errorCount++;
              }
            } catch (err) {
              errorCount++;
            }
          }
        }
      } catch (err) {
        console.error('Batch import error:', err);
        errorCount += importData.templates.length;
      }

      // Import notification templates using batch endpoint
      if (importData.notificationTemplates && importData.notificationTemplates.length > 0) {
        try {
          const notifResponse = await fetch(`${BACKEND_URL}/api/recurring-subscriptions/notification-templates/import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              templates: importData.notificationTemplates
            })
          });

          if (notifResponse.ok) {
            const notifResult = await notifResponse.json();
            successCount += notifResult.success_count || 0;
            createdCount += notifResult.created_count || 0;
            
            if (notifResult.errors && notifResult.errors.length > 0) {
              console.warn('Notification import warnings:', notifResult.errors);
            }
          } else {
            // Fallback to individual PUT requests
            console.warn('Notification batch import failed, falling back to individual requests');
            for (const template of importData.notificationTemplates) {
              try {
                const response = await fetch(`${BACKEND_URL}/api/recurring-subscriptions/notification-templates/${template.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    subject: template.subject,
                    body: template.body
                  })
                });

                if (response.ok) {
                  successCount++;
                } else {
                  errorCount++;
                }
              } catch (err) {
                errorCount++;
              }
            }
          }
        } catch (err) {
          console.error('Notification import error:', err);
          errorCount += importData.notificationTemplates.length;
        }
      }

      // Refresh templates
      fetchTemplates();
      fetchNotificationTemplates();

      // Import automation rules if present
      let rulesImported = 0;
      if (importData.automationRules && importData.automationRules.length > 0) {
        try {
          const rulesResponse = await fetch(`${BACKEND_URL}/api/automation/rules/import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              rules: importData.automationRules
            })
          });

          if (rulesResponse.ok) {
            const rulesResult = await rulesResponse.json();
            rulesImported = rulesResult.imported || importData.automationRules.length;
          } else {
            console.warn('Automation rules import failed');
          }
        } catch (err) {
          console.error('Automation rules import error:', err);
        }
      }

      // Import WhatsApp templates if present
      let whatsappImported = 0;
      if (importData.whatsappTemplates && importData.whatsappTemplates.length > 0) {
        try {
          const waResponse = await fetch(`${BACKEND_URL}/api/whatsapp/templates/import`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              templates: importData.whatsappTemplates
            })
          });

          if (waResponse.ok) {
            const waResult = await waResponse.json();
            whatsappImported = waResult.imported || importData.whatsappTemplates.length;
          } else {
            console.warn('WhatsApp templates import failed');
          }
        } catch (err) {
          console.error('WhatsApp templates import error:', err);
        }
      }

      let resultMessage = `✅ Import abgeschlossen!\n\n📧 ${successCount} E-Mail-Vorlagen`;
      if (createdCount > 0) {
        resultMessage += ` (${createdCount} neu)`;
      }
      if (duplicatesDeleted > 0) {
        resultMessage += `\n🧹 ${duplicatesDeleted} Duplikate bereinigt`;
      }
      if (rulesImported > 0) {
        resultMessage += `\n⚡ ${rulesImported} Automation-Regeln`;
      }
      if (whatsappImported > 0) {
        resultMessage += `\n💬 ${whatsappImported} WhatsApp-Vorlagen`;
      }
      if (errorCount > 0) {
        resultMessage += `\n❌ ${errorCount} fehlgeschlagen`;
      }
      
      alert(resultMessage);
    } catch (error) {
      console.error('Error importing templates:', error);
      alert('❌ Fehler beim Import: ' + error.message);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const insertTag = (openTag, closeTag) => {
    const textarea = document.querySelector('textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateContent.body;
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + openTag + selectedText + closeTag + text.substring(end);
    
    setTemplateContent({ ...templateContent, body: newText });
    
    // Set cursor position after inserted tags
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, start + openTag.length + selectedText.length);
    }, 10);
  };


  if (editMode && selectedTemplate) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => {
              setEditMode(false);
              setSelectedTemplate(null);
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
            Zurück zur Liste
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedTemplate.name} bearbeiten
            </h2>
            <div className="flex flex-wrap gap-2">
              {isSystemEmailTemplate(selectedTemplate) && (
                <button
                  onClick={handleAiFill}
                  disabled={aiFilling}
                  className="flex items-center gap-2 px-4 py-2 border border-purple-400 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-60"
                  data-testid="ai-fill-button"
                  title="Schreibe den Text frei – die KI setzt die passenden Variablen automatisch ein"
                >
                  {aiFilling ? <Loader2 size={20} className="animate-spin" /> : <Wand2 size={20} />}
                  KI-Variablen einsetzen
                </button>
              )}
              {templateMeta?.has_default && (
                <button
                  onClick={handleResetTemplate}
                  disabled={resetting}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                  data-testid="reset-template-button"
                  title="Auf den System-Standard zurücksetzen"
                >
                  {resetting ? <Loader2 size={20} className="animate-spin" /> : <RotateCcw size={20} />}
                  Standard
                </button>
              )}
              <button
                onClick={openTestDialog}
                disabled={sendingSelf}
                className="flex items-center gap-2 px-4 py-2 border border-emerald-400 text-emerald-600 rounded-lg hover:bg-emerald-50 disabled:opacity-60"
                data-testid="send-test-to-self-button"
                title={user?.email ? `Testmail mit eigenen Beispielwerten an deine Adresse (${user.email})` : 'Testmail mit eigenen Beispielwerten senden'}
              >
                {sendingSelf ? <Loader2 size={20} className="animate-spin" /> : <MailCheck size={20} />}
                Testmail an mich
              </button>
              <button
                onClick={() => sendPreview(selectedTemplate, true)}
                className="flex items-center gap-2 px-4 py-2 border border-amber-400 text-amber-600 rounded-lg hover:bg-amber-50"
                data-testid="send-preview-button"
                title="Sendet eine Mail mit Dummy-Variablen an eine selbst gewählte Adresse"
              >
                <Send size={20} />
                An Adresse …
              </button>
              <button
                onClick={() => {
                  if (previewMode) { setPreviewMode(false); }
                  else { handleRenderPreview(); }
                }}
                disabled={rendering}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                data-testid="preview-toggle-button"
              >
                {rendering ? <Loader2 size={20} className="animate-spin" /> : <Eye size={20} />}
                {previewMode ? 'Editor' : 'Vorschau'}
              </button>
              <button
                onClick={saveTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="save-template-button"
              >
                <Save size={20} />
                Speichern
              </button>
            </div>
          </div>

          {selectedTemplate.id === 'invoice_notification' && (
            <p className="mt-2 text-xs text-amber-700">
              Hinweis: Diese Vorlage wird für <span className="font-semibold">neue Rechnungen (Zahlungsart Rechnung/TWINT)</span> verwendet.
              Der PDF-Anhang und die Firmenangaben kommen aus <span className="font-semibold">Einstellungen → Rechnung &amp; Firmendaten</span>.
            </p>
          )}


          {!previewMode ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Betreff
                </label>
                <input
                  type="text"
                  value={templateContent.subject}
                  onChange={(e) => setTemplateContent({ ...templateContent, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="template-subject-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nachrichtentext
                </label>
                
                {/* Formatierungs-Toolbar */}
                <div className="flex gap-2 mb-2 p-2 bg-gray-100 rounded-t-lg border border-gray-300 border-b-0">
                  <button
                    onClick={() => insertTag('<b>', '</b>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50"
                    title="Fett"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    onClick={() => insertTag('<i>', '</i>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50"
                    title="Kursiv"
                  >
                    <em>I</em>
                  </button>
                  <button
                    onClick={() => insertTag('<u>', '</u>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50"
                    title="Unterstrichen"
                  >
                    <u>U</u>
                  </button>
                  <div className="border-l border-gray-300 mx-2"></div>
                  <button
                    onClick={() => insertTag('<h1>', '</h1>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50 text-xl font-bold"
                    title="Überschrift 1"
                  >
                    H1
                  </button>
                  <button
                    onClick={() => insertTag('<h2>', '</h2>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50 text-lg font-bold"
                    title="Überschrift 2"
                  >
                    H2
                  </button>
                  <button
                    onClick={() => insertTag('<p>', '</p>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50"
                    title="Absatz"
                  >
                    P
                  </button>
                  <div className="border-l border-gray-300 mx-2"></div>
                  <button
                    onClick={() => insertTag('<span style="color: red;">', '</span>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50 text-red-600"
                    title="Rot"
                  >
                    🔴
                  </button>
                  <button
                    onClick={() => insertTag('<span style="color: blue;">', '</span>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50 text-blue-600"
                    title="Blau"
                  >
                    🔵
                  </button>
                  <button
                    onClick={() => insertTag('<span style="font-size: 18px;">', '</span>')}
                    className="px-3 py-1 bg-white border rounded hover:bg-gray-50 text-lg"
                    title="Grösser"
                  >
                    A+
                  </button>
                </div>
                
                <textarea
                  value={templateContent.body}
                  onChange={(e) => setTemplateContent({ ...templateContent, body: e.target.value })}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-b-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  data-testid="template-body-textarea"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sie können HTML-Tags verwenden: &lt;b&gt;Fett&lt;/b&gt;, &lt;i&gt;Kursiv&lt;/i&gt;, &lt;h1&gt;Überschrift&lt;/h1&gt;
                </p>
              </div>

              {(() => {
                const required = templateMeta?.required_variables || [];
                const missing = computeMissingRequired();
                const meta = templateMeta?.variable_meta || {};
                const isReq = (v) => required.includes(v);
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="template-variables-panel">
                    <h3 className="font-medium text-blue-900 mb-1">Variablen</h3>
                    <p className="text-xs text-blue-700 mb-3">
                      Klicke eine Variable, um sie an der Cursor-Position einzufügen.
                      {required.length > 0 && <> <span className="font-semibold">Pflicht-Variablen</span> müssen im Betreff oder Text vorkommen.</>}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {templateContent.variables.map((variable) => {
                        const req = isReq(variable);
                        const isMissing = missing.includes(variable);
                        return (
                          <button
                            key={variable}
                            type="button"
                            onClick={() => insertTag('{{' + variable + '}}', '')}
                            data-testid={`variable-chip-${variable}`}
                            title={meta[variable]?.label || variable}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              isMissing
                                ? 'bg-red-100 text-red-700 border-red-300'
                                : req
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                : 'bg-blue-100 text-blue-800 border-blue-200'
                            }`}
                          >
                            {'{{' + variable + '}}'}
                            {req && <span className="ml-1 font-bold">*</span>}
                            {meta[variable]?.label && <span className="ml-1 opacity-70 text-xs">· {meta[variable].label}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {isSystemEmailTemplate(selectedTemplate) && missing.length > 0 && (
                      <div className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="missing-variables-warning">
                        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                        <span>
                          Es fehlen noch Pflicht-Variablen: {missing.map((m) => `{{${m}}}`).join(', ')}.
                          Das Speichern ist erst möglich, wenn alle enthalten sind.
                        </span>
                      </div>
                    )}
                    {isSystemEmailTemplate(selectedTemplate) && required.length > 0 && missing.length === 0 && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700" data-testid="variables-ok">
                        <CheckCircle size={16} /> Alle Pflicht-Variablen sind enthalten.
                      </div>
                    )}
                  </div>
                );
              })()}

              {emailLayouts.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-4 relative" data-testid="editor-layout-picker">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers size={16} className="text-gray-500" />
                    <h3 className="font-medium text-gray-800">Layout für die Vorschau</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Das aktive E-Mail-Layout wird unter „Aussehen" festgelegt. Hier nur zum Testen der Vorschau umschaltbar. <span className="text-gray-400">Fahre über ein Layout für eine große Live-Vorschau.</span></p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {emailLayouts.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setSelectedLayout(l.id)}
                        onMouseEnter={() => { setHoverLayout(l.id); loadLayoutPreview(l.id); }}
                        onMouseLeave={() => setHoverLayout((cur) => (cur === l.id ? null : cur))}
                        data-testid={`editor-layout-${l.id}`}
                        className={`text-left rounded-lg border-2 p-1.5 transition-all ${selectedLayout === l.id ? 'border-gray-900 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-300'}`}
                        title={l.description}
                      >
                        <EmailLayoutThumbnail layoutId={l.id} />
                        <div className="mt-1.5 flex items-center justify-between px-0.5">
                          <span className="text-xs font-medium text-gray-800 truncate">{l.name}</span>
                          {selectedLayout === l.id && <CheckCircle size={14} className="text-gray-900 shrink-0" />}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Große Live-Vorschau beim Überfahren (echtes Logo + Akzentfarbe) */}
                  {hoverLayout && (
                    <div
                      className="absolute right-4 bottom-full mb-3 z-30 w-[360px] max-w-[90vw] bg-white border border-gray-300 rounded-xl shadow-2xl overflow-hidden"
                      data-testid="layout-hover-preview"
                      onMouseEnter={() => setHoverLayout(hoverLayout)}
                    >
                      <div className="px-3 py-2 bg-gray-900 text-white text-xs font-medium flex items-center gap-2">
                        <Eye size={14} />
                        Live-Vorschau: {emailLayouts.find((x) => x.id === hoverLayout)?.name || hoverLayout}
                      </div>
                      {layoutPreviews[hoverLayout] ? (
                        <iframe
                          title="Layout-Live-Vorschau"
                          srcDoc={layoutPreviews[hoverLayout]}
                          className="w-full bg-white"
                          style={{ height: '420px', border: 'none' }}
                          sandbox=""
                          data-testid="layout-hover-preview-iframe"
                        />
                      ) : (
                        <div className="h-[420px] flex items-center justify-center text-gray-400 text-sm">
                          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Vorschau wird geladen…
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div data-testid="rendered-preview">
              <p className="text-xs text-gray-500 mb-3">
                So sieht die E-Mail mit Beispielwerten, deinem Branding und dem gewählten Layout aus (reine Vorschau, kein Versand).
              </p>
              {renderedHtml ? (
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm" data-testid="rendered-preview-subject">
                    <span className="font-medium text-gray-700">Betreff:</span> {renderedSubject}
                  </div>
                  <iframe
                    title="E-Mail-Vorschau"
                    srcDoc={renderedHtml}
                    className="w-full"
                    style={{ height: '640px', border: 'none' }}
                    data-testid="rendered-preview-iframe"
                    sandbox=""
                  />
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 text-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-gray-400" />
                  Vorschau wird erstellt…
                </div>
              )}
            </div>
          )}

          {/* Testmail-Dialog: optionale eigene Beispielwerte */}
          {showTestDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="test-mail-dialog" onClick={() => setShowTestDialog(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <MailCheck size={18} className="text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Testmail senden</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empfänger</label>
                    <input
                      type="email"
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                      placeholder="deine@adresse.ch"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      data-testid="test-mail-recipient-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">Standard: deine eigene Adresse. Du kannst auch eine andere eintragen.</p>
                  </div>
                  {Object.keys(testVars).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Eigene Beispielwerte</p>
                      <p className="text-xs text-gray-400 mb-2">Leer lassen = Standard-Beispielwert wird verwendet.</p>
                      <div className="space-y-2">
                        {Object.keys(testVars).map((v) => {
                          const meta = (templateMeta?.variable_meta || {})[v] || {};
                          return (
                            <div key={v} className="flex items-center gap-2">
                              <span className="text-xs font-mono text-gray-500 w-36 shrink-0 truncate" title={meta.label || v}>{meta.label || v}</span>
                              <input
                                type="text"
                                value={testVars[v]}
                                onChange={(e) => setTestVars((prev) => ({ ...prev, [v]: e.target.value }))}
                                placeholder={meta.sample || `{{${v}}}`}
                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                data-testid={`test-mail-var-${v}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
                  <button
                    onClick={() => setShowTestDialog(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
                    data-testid="test-mail-cancel-button"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={sendTestEmail}
                    disabled={sendingSelf}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 text-sm"
                    data-testid="test-mail-send-button"
                  >
                    {sendingSelf ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Senden
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Zurück Button */}
      {!embedded && (
      <button
        onClick={() => window.history.back()}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} />
        Zurück
      </button>
      )}
      
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email-Vorlagen</h1>
            <p className="text-sm text-gray-600 mt-1">
              Bearbeiten Sie Ihre Email-Templates für automatische Benachrichtigungen
            </p>
          </div>
          
          {/* Export / Import Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={startTranslation}
              disabled={translating}
              data-testid="translate-all-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-60"
              title="Alle Vorlagen per KI in EN/FR/IT übersetzen"
            >
              🌍 {translating ? `Übersetze… ${translateStatus?.processed ?? 0}/${translateStatus?.total ?? '?'}` : 'In EN/FR/IT übersetzen'}
            </button>

            <button
              onClick={translateConsentForms}
              disabled={translatingConsent}
              data-testid="translate-consent-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-60"
              title="Einverständnis-Formulare (Kryo/EMS/Haftung) per KI in EN/FR/IT übersetzen"
            >
              ✍️ {translatingConsent ? 'Übersetze Consent…' : 'Consent-Formulare übersetzen'}
            </button>

            <button
              onClick={() => {
                setShowLogs(!showLogs);
                if (!showLogs) fetchNotificationLogs();
              }}
              className={`flex items-center gap-2 px-4 py-2 ${showLogs ? 'bg-orange-600' : 'bg-orange-500'} text-white rounded-lg hover:bg-orange-600 text-sm font-medium`}
              title="Versand-Logs anzeigen"
            >
              <ScrollText size={18} />
              {showLogs ? 'Logs schliessen' : 'Logs'}
            </button>
            
            <button
              onClick={handleExportTemplates}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
              title="Alle Vorlagen als JSON exportieren"
            >
              <Download size={18} />
              Exportieren
            </button>
            
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm font-medium">
              <Upload size={18} />
              {importing ? 'Importiere...' : 'Importieren'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportTemplates}
                className="hidden"
                disabled={importing}
              />
            </label>
          </div>
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Hinweis: Die Standard-Rechnungs-Email (z.B. für direkte Kreditkartenzahlungen) wird unter
          <span className="font-semibold"> Einstellungen → Rechnung &amp; Firmendaten</span> konfiguriert (Betreff &amp; Text).
        </p>
        
        {/* Export/Import Info */}
        <p className="text-xs text-blue-600 mt-1">
          💡 Tipp: Exportieren Sie Vorlagen im Preview, dann importieren Sie sie im Live-System
        </p>
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="email-template-pdf-history-hint">
          Hinweis: Bei den Punkten-/Abo-Warnungen wird die Trainings-Historie automatisch als PDF angehängt.
        </div>
      </div>

      {/* Notification Logs Panel */}
      {showLogs && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <ScrollText size={20} />
              Versand-Logs (Passwort-Reset, Verifizierung)
            </h3>
            <button
              onClick={fetchNotificationLogs}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              disabled={logsLoading}
            >
              <RefreshCw size={14} className={logsLoading ? 'animate-spin' : ''} />
              Aktualisieren
            </button>
          </div>
          
          {logsLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              <p className="text-sm text-gray-500 mt-2">Lade Logs...</p>
            </div>
          ) : notificationLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p>Keine Logs vorhanden</p>
              <p className="text-xs mt-1">Logs werden beim nächsten Passwort-Reset oder Verifizierung erstellt</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Zeitpunkt</th>
                    <th className="px-3 py-2 text-left">Typ</th>
                    <th className="px-3 py-2 text-left">Empfänger</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">WhatsApp</th>
                    <th className="px-3 py-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {notificationLogs.map((log, idx) => (
                    <tr key={log.id || idx} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString('de-CH')}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          log.type === 'password_reset' ? 'bg-blue-100 text-blue-800' :
                          log.type === 'verification' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.type === 'password_reset' ? '🔐 Passwort-Reset' : 
                           log.type === 'verification' ? '✅ Verifizierung' : log.type}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-gray-900">{log.customer_name || '-'}</div>
                        <div className="text-xs text-gray-500">{log.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        {log.email_status === 'sent' ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle size={14} /> Gesendet
                          </span>
                        ) : log.email_status === 'failed' ? (
                          <span className="flex items-center gap-1 text-red-600" title={log.email_error}>
                            <XCircle size={14} /> Fehlgeschlagen
                          </span>
                        ) : log.email_status === 'disabled' ? (
                          <span className="flex items-center gap-1 text-gray-500">
                            <Clock size={14} /> Deaktiviert
                          </span>
                        ) : (
                          <span className="text-gray-400">{log.email_status || '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {log.whatsapp_status === 'sent' ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle size={14} /> Gesendet
                          </span>
                        ) : log.whatsapp_status === 'failed' ? (
                          <span className="flex items-center gap-1 text-red-600" title={log.whatsapp_error}>
                            <XCircle size={14} /> Fehler
                          </span>
                        ) : log.whatsapp_status === 'skipped' ? (
                          <span className="text-gray-400">Übersprungen</span>
                        ) : log.whatsapp_status === 'disabled' ? (
                          <span className="text-gray-500">Deaktiviert</span>
                        ) : (
                          <span className="text-gray-400">{log.whatsapp_status || '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 text-xs hover:underline">Details</summary>
                          <div className="mt-2 p-2 bg-white rounded border text-xs max-w-md">
                            {log.email_error && (
                              <p className="text-red-600 mb-1"><strong>Email-Fehler:</strong> {log.email_error}</p>
                            )}
                            {log.whatsapp_error && (
                              <p className="text-red-600 mb-1"><strong>WhatsApp-Fehler:</strong> {log.whatsapp_error}</p>
                            )}
                            {log.template_used && (
                              <p className="text-gray-600"><strong>Template:</strong> {log.template_used}</p>
                            )}
                            {log.reset_url && (
                              <p className="text-gray-600 break-all"><strong>URL:</strong> {log.reset_url}</p>
                            )}
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* KI Editor Panel */}
      <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="text-purple-600" size={20} />
            <h3 className="font-bold text-purple-900">KI-Massenbearbeitung</h3>
          </div>
          <div className="flex items-center gap-2">
            {templateBackup && (
              <button
                onClick={handleRollback}
                className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
              >
                <RotateCcw size={16} />
                Zur vorherigen Version
              </button>
            )}
            <button
              onClick={() => setShowAIEditor(!showAIEditor)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showAIEditor 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              <Sparkles size={16} />
              {showAIEditor ? 'Schliessen' : 'KI-Editor öffnen'}
            </button>
          </div>
        </div>
        
        {showAIEditor && (
          <div className="space-y-3">
            <p className="text-sm text-purple-700">
              Beschreiben Sie, was Sie ändern möchten. Die KI wird alle Email-Vorlagen entsprechend anpassen.
            </p>
            
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Beispiele:
• Ändere 'Sportliche Grüsse' zu 'Herzliche Grüsse'
• Ersetze alle Links von strongfit.ch zu meineapp.ch
• Ändere die Telefonnummer zu +41 79 123 45 67
• Entferne alle Emojis aus den Vorlagen
• Füge am Ende jeder Email 'Ihr StrongFit Team' hinzu"
              className="w-full h-32 p-3 border border-purple-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
            
            <div className="flex gap-2">
              <button
                onClick={handleAIBulkEdit}
                disabled={aiProcessing || !aiPrompt.trim()}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  aiProcessing || !aiPrompt.trim()
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {aiProcessing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    KI verarbeitet...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Änderungen vorschlagen
                  </>
                )}
              </button>
            </div>
            
            {/* AI Preview */}
            {aiPreview && aiPreview.modified_templates && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-purple-300">
                <h4 className="font-bold text-purple-900 mb-2">
                  ✨ Vorgeschlagene Änderungen ({aiPreview.modified_templates.length} Templates)
                </h4>
                <div className="max-h-64 overflow-y-auto space-y-2 mb-3">
                  {aiPreview.modified_templates.slice(0, 5).map((t, i) => (
                    <div key={i} className="p-2 bg-purple-50 rounded text-xs">
                      <div className="font-medium text-purple-800">{t.name}</div>
                      <div className="text-purple-700 mt-1">Betreff: {t.subject}</div>
                      <div className="text-purple-600 mt-1 whitespace-pre-line line-clamp-2">{t.body?.substring(0, 200)}...</div>
                    </div>
                  ))}
                  {aiPreview.modified_templates.length > 5 && (
                    <div className="text-sm text-purple-600 text-center">
                      ... und {aiPreview.modified_templates.length - 5} weitere Templates
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={applyAIChanges}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    ✅ Änderungen übernehmen
                  </button>
                  <button
                    onClick={() => setAiPreview(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                  >
                    Verwerfen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Re-Engagement Email Template (small card) */}
        <div
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            if (!reEngagementTemplates?.email_template) {
              return;
            }
            loadTemplate({
              id: 're_engagement_email',
              name: '🔁 Re-Engagement Email',
              description: 'Inaktivität ohne Termine (30/60 Tage)',
              subject: reEngagementTemplates.email_template.subject,
              body: reEngagementTemplates.email_template.body,
              variables: ['customer_name', 'days_inactive', 'studio_name', 'portal_url'],
              _source: 're_engagement'
            });
          }}
          data-testid="reengagement-email-card-small"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Mail className="text-amber-600" size={20} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">🔁 Re-Engagement Email</h3>
              <p className="text-xs text-gray-500 mt-1">30/60 Tage ohne Termin</p>
            </div>
          </div>
          {reEngagementLoading ? (
            <p className="text-xs text-gray-400">Lädt...</p>
          ) : reEngagementTemplates ? (
            <div className="text-sm text-gray-600">
              <p className="truncate">
                <span className="font-medium">Betreff:</span> {reEngagementTemplates.email_template?.subject || 'Kein Betreff'}
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-400">Keine Vorlage gefunden</p>
          )}
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">Re-Engagement</span>
            <span>Bearbeiten</span>
          </div>
        </div>

        {/* Alle Benachrichtigungs-Vorlagen */}
        {notificationTemplates.map((notifTemplate) => {
          // Mapping für bekannte Template-Typen
          const templateConfig = {
            'failed_reload_customer_email': { 
              icon: '❌', 
              name: 'Auto-Reload fehlgeschlagen (Kunde)', 
              color: 'blue',
              variables: ['kunde_name', 'abo_name', 'betrag', 'fehler_grund', 'datum', 'failed_count', 'payment_link']
            },
            'failed_reload_admin_email': { 
              icon: '❌', 
              name: 'Auto-Reload fehlgeschlagen (Admin)', 
              color: 'orange',
              variables: ['kunde_name', 'abo_name', 'betrag', 'fehler_grund', 'datum', 'failed_count', 'payment_link']
            },
            'failed_reload_customer_whatsapp': { 
              icon: '📱', 
              name: 'Auto-Reload fehlgeschlagen (WhatsApp)', 
              color: 'green',
              variables: ['kunde_name', 'abo_name', 'betrag', 'fehler_grund']
            },
            'low_points_warning_email': { 
              icon: '⚠️', 
              name: 'Punkte-Warnung (E-Mail)', 
              color: 'yellow',
              variables: ['kunde_name', 'paket_name', 'punkte_balance', 'punkte_kaufen_link']
            },
            'password_reset_email': { 
              icon: '🔑', 
              name: 'Passwort zurücksetzen', 
              color: 'purple',
              variables: ['kunde_name', 'reset_link']
            },
            'account_reactivation_email': { 
              icon: '🎉', 
              name: 'Konto reaktiviert', 
              color: 'teal',
              variables: ['customer_name', 'studio_name']
            }
          };
          
          const config = templateConfig[notifTemplate.template_type] || {
            icon: '🔔',
            name: notifTemplate.name || notifTemplate.template_type,
            color: 'gray',
            variables: []
          };
          
          const colorClasses = {
            blue: 'bg-blue-100 text-blue-600',
            orange: 'bg-orange-100 text-orange-600',
            green: 'bg-green-100 text-green-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            purple: 'bg-purple-100 text-purple-600',
            teal: 'bg-teal-100 text-teal-600',
            gray: 'bg-gray-100 text-gray-600'
          };
          
          return (
            <div
              key={notifTemplate.id || notifTemplate.template_type}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => loadTemplate({
                ...notifTemplate,
                name: `${config.icon} ${config.name}`,
                description: `Benachrichtigungs-Vorlage: ${notifTemplate.template_type}`,
                variables: config.variables
              })}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 ${colorClasses[config.color].split(' ')[0]} rounded-lg`}>
                  <Mail className={colorClasses[config.color].split(' ')[1]} size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">
                    {config.icon} {config.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {notifTemplate.template_type}
                  </p>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p className="truncate">
                  <span className="font-medium">Betreff:</span> {notifTemplate.subject || 'Kein Betreff'}
                </p>
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">Benachrichtigung</span>
                <span>Geändert: {notifTemplate.updated_at ? new Date(notifTemplate.updated_at).toLocaleDateString('de-CH') : '-'}</span>
              </div>
            </div>
          );
        })}

        {/* Normale Email Templates */}
        {templates.map((template) => (
          <div
            key={template.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => loadTemplate(template)}
            data-testid={`template-card-${template.id}`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="text-blue-600" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{template.name}</h3>
                <p className="text-xs text-gray-500 mt-1">{template.description}</p>
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              <p className="truncate">
                <span className="font-medium">Betreff:</span> {template.subject}
              </p>
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                Zuletzt geändert: {new Date(template.updated_at).toLocaleDateString('de-CH')}
                {(template.available_languages || []).length > 0 && (
                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium" title="Verfügbare Übersetzungen">
                    🌍 {(template.available_languages || []).map((l) => l.toUpperCase()).join('/')}
                  </span>
                )}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  sendPreview(template, false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                data-testid={`preview-button-${template.id}`}
                title="Preview-Mail an deine Adresse senden"
              >
                <Send size={14} />
                <span>Preview</span>
              </button>
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
            <Mail className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-500">Noch keine Email-Templates vorhanden</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
